import InputField from 'components/molecules/input-field'
import { VoucherInputValueField } from 'domains/voucher/molecules'
import { FieldArray, getIn, useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import {
  Button,
  ListGroupItem
} from 'reactstrap'
import StringUtil from 'utils/string-util'

const defaultConditionField = {
  code     : '',
  operator : '',
  value    : ''
}

const hiddenFields = [
  'is_marketing_discount'
]

const conditionFieldsPropTypes = {
  label           : PropTypes.string,
  name            : PropTypes.string,
  values          : PropTypes.array,
  attributes      : PropTypes.array,
  operators       : PropTypes.array,
  provinces       : PropTypes.array,
  shipmentMethods : PropTypes.array,
  paymentMethods  : PropTypes.array,
  formikBag       : PropTypes.object
}

const attributesOptionsPropTypes = {
  indexName  : PropTypes.string,
  attributes : PropTypes.array
}

const operatorsOptionsPropTypes = {
  operators: PropTypes.array
}

/**
 * Condition fields
 * @param {*} name - Name of the field. Used to hooked up to the formik
 * @param {*} values - Values of the field
 * @param {*} attributes - Conditions for Product/Cart Attributes ({code:, operator: value: })
 * @param {object} formikBag - an object containing a subset of the injected props and methods and any props that were passed to the wrapped formik component.
 * @param {array} operators - Operator options
 * @param {array} shipmentMethods - Shipment Method options
 */
const ConditionFields = ({
  name,
  values,
  attributes,
  shipmentMethods
}) => {

  /**
   * attributes prop in the shape of
   *  [{
   *    code:
   *    input_type:
   *    operators: [ ]
   *  }, {..}]
   *
   * Iterated as a FieldArray with the shape
   * {
   *   code:
   *   operator:
   *   value:
   * }
   */
  const findOperatorOptions = (attribute) => (
    attributes.find((available_attribute) => available_attribute.code == attribute.code)?.operators
  )

  return (
    <FieldArray name={ name }>
      {
        (arrayHelpers) => (
          <>
            {
              values.map((attribute, index) => (
                !hiddenFields.includes(attribute.code) &&
                  <ListGroupItem key={ index }>
                    <InputField
                      type='select'
                      label='Attribute'
                      required={ true }
                      name={ `${name}.${index}.code` }
                    >
                      <AttributesOptions
                        indexName={ `${name}.${index}` }
                        attributes={ attributes }
                      />
                    </InputField>

                    <InputField
                      type='select'
                      label='Operator'
                      required={ true }
                      name={ `${name}.${index}.operator` }
                    >
                      <OperatorsOptions
                        operators={ findOperatorOptions(attribute) }
                      />
                    </InputField>

                    <VoucherInputValueField
                      name={ `${name}.${index}.value` }
                      attribute={ attribute }
                      shipmentMethods={ shipmentMethods }
                    />

                    <Button
                      color='danger'
                      onClick={ () => arrayHelpers.remove(index) }
                    >
                      { 'Remove Field' }
                    </Button>
                  </ListGroupItem>
              ))
            }
            <ListGroupItem>
              <Button
                block
                color={ 'primary' }
                outline
                className={ 'mb-0' }
                onClick={ () => arrayHelpers.push(defaultConditionField) }
              >
                { 'Add' }
              </Button>
            </ListGroupItem>
          </>
        )
      }
    </FieldArray>
  )
}
/**
 * Render Operator option for input select field
 * @param {*} operators - Operator options
 */
const OperatorsOptions = ({ operators }) => (
  <>
    <option value="">choose the operator</option>
    {
      operators && operators.map((operator, index) => (
        <option
          key={ `conditions-operator-${index}` }
          value={ operator.value }
        >
          { StringUtil.titleCase(operator.label) }
        </option>
      ))
    }
  </>
)

/**
 * Render Attribute option for input select field
 * @param {array} attributes - Attribute options (Cart and Product)
 * @param {string} indexName - Field name with Index (From FieldArray)
 */
const AttributesOptions = ({ indexName, attributes }) => {
  const { values: formikValues, setFieldValue, initialValues } = useFormikContext()

  // * deeply get a value from formik Values by its combination between name and index
  const initialCondition = getIn(initialValues, indexName)
  const currentCondition = getIn(formikValues, indexName)

  const { code: initialCode, value: initialValue, operator: initialOperator } = initialCondition || {}
  const { code: currentCode }                                                 = currentCondition

  useEffect(() => {
    // * set the specific array field to default value if attribute option has changed
    if (initialCode !== currentCode) {
      setFieldValue(
        indexName,
        {
          ...defaultConditionField,
          code: currentCode
        }
      )
    }
    // * If the attribute code changed to initial value, revert back product attribute to initial value
    else {
      setFieldValue(
        indexName,
        {
          code     : initialCode,
          value    : initialValue,
          operator : initialOperator
        }
      )
    }
  }, [
    indexName,
    initialCode,
    initialOperator,
    initialValue,
    currentCode,
    setFieldValue
  ])

  return (
    <>
      <option value="">choose the attribute</option>
      {
        attributes && attributes.map((attribute, index) => (
          attribute.should_show_input &&
          <option
            key={ `conditions-operator-${index}` }
            value={ attribute.code }
          >
            { StringUtil.titleCase(attribute.label) }
          </option>
        ))
      }
    </>
  )
}

ConditionFields.propTypes   = conditionFieldsPropTypes
AttributesOptions.propTypes = attributesOptionsPropTypes
OperatorsOptions.propTypes  = operatorsOptionsPropTypes

export default ConditionFields
